import { useEffect, useRef } from 'react';

/**
 * Slice 7.6 — small shared admin UI primitives. Kept simple and headless;
 * we don't reach for a UI framework for this. ESC closes modals, focus
 * traps to the modal on open.
 */

export const RISK_LEVELS = [
  'Very_low', 'Low', 'Moderate', 'High', 'Very_high',
] as const;

export const RISK_LEVELS_EXTENDED = [
  'No_risk', 'Very_low', 'Low', 'Moderate', 'High', 'Very_high', 'Critical',
] as const;

export type RiskLevel = typeof RISK_LEVELS_EXTENDED[number];

export function RiskLevelSelect({ value, onChange, name, required, extended }: {
  value: string;
  onChange: (v: string) => void;
  name?: string;
  required?: boolean;
  extended?: boolean;
}) {
  const opts = extended ? RISK_LEVELS_EXTENDED : RISK_LEVELS;
  return (
    <select
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="border border-ink-200 rounded-sm px-2 py-1 text-sm bg-white focus:outline-none focus:border-accent w-full"
    >
      <option value="" disabled>Select…</option>
      {opts.map((o) => (
        <option key={o} value={o}>{o.replace('_', ' ')}</option>
      ))}
    </select>
  );
}

export function Modal({ open, onClose, title, children, widthClass }: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  widthClass?: string;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 flex items-start justify-center pt-16 px-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        ref={ref}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className={`bg-white rounded-sm shadow-xl border border-ink-200 ${widthClass ?? 'max-w-2xl w-full'} my-8 outline-none`}
      >
        <div className="border-b border-ink-200 px-5 py-3 flex items-baseline justify-between">
          <h3 className="font-display text-lg text-ink-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-400 hover:text-ink-700 text-2xl leading-none -mt-1"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, title, message, confirmLabel, danger, onConfirm, onCancel }: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal open={open} onClose={onCancel} title={title} widthClass="max-w-md w-full">
      <p className="text-sm text-ink-700 mb-5">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="text-sm px-4 py-1 border border-ink-200 rounded-sm hover:bg-ink-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={`text-sm px-4 py-1 border rounded-sm ${
            danger
              ? 'bg-red-600 border-red-700 text-white hover:bg-red-700'
              : 'bg-accent border-accent text-white hover:bg-accent/90'
          }`}
        >
          {confirmLabel ?? 'Confirm'}
        </button>
      </div>
    </Modal>
  );
}

export function FormRow({ label, children, hint }: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 items-start mb-3">
      <label className="text-xs text-ink-600 mt-1.5">{label}</label>
      <div>
        {children}
        {hint && <div className="text-[10px] text-ink-400 mt-0.5">{hint}</div>}
      </div>
    </div>
  );
}

export function TextInput({ value, onChange, name, placeholder, required, maxLength }: {
  value: string;
  onChange: (v: string) => void;
  name?: string;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
}) {
  return (
    <input
      type="text"
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      required={required}
      maxLength={maxLength}
      className="border border-ink-200 rounded-sm px-2 py-1 text-sm w-full focus:outline-none focus:border-accent"
    />
  );
}

export function TextArea({ value, onChange, name, placeholder, rows }: {
  value: string;
  onChange: (v: string) => void;
  name?: string;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows ?? 3}
      className="border border-ink-200 rounded-sm px-2 py-1 text-sm w-full focus:outline-none focus:border-accent resize-y"
    />
  );
}

export function Select<T extends string>({ value, onChange, options, name, required }: {
  value: T | '';
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  name?: string;
  required?: boolean;
}) {
  return (
    <select
      name={name}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      required={required}
      className="border border-ink-200 rounded-sm px-2 py-1 text-sm bg-white focus:outline-none focus:border-accent w-full"
    >
      <option value="" disabled>Select…</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function FormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2 mb-3">
      {message}
    </div>
  );
}

export function SubmitBar({ onCancel, onSubmit, submitting, submitLabel }: {
  onCancel: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  submitLabel?: string;
}) {
  return (
    <div className="flex justify-end gap-2 border-t border-ink-100 pt-4 mt-4">
      <button
        type="button"
        onClick={onCancel}
        className="text-sm px-4 py-1 border border-ink-200 rounded-sm hover:bg-ink-50"
        disabled={submitting}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="text-sm px-4 py-1 bg-accent text-white border border-accent rounded-sm hover:bg-accent/90 disabled:opacity-50"
      >
        {submitting ? 'Saving…' : (submitLabel ?? 'Save')}
      </button>
    </div>
  );
}
